/*
  On-page CTA anchor for dues (seva-dues-cta):
    loading → pending-payment (mode: pending_payment)
            → member-status (mode: none)
            → action (payable / guest)
            → unavailable (error)

  Tray-only steps live in <seva-dues-tray> + <seva-dues-flow>.
*/

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SevaFreezable } from './lib/freezable-mixin.js';
import { duesCtaStyles } from './lib/dues-flow-styles.js';
import { ApiClient } from './lib/api-client.js';
import { TokenManager } from './lib/token-manager.js';
import { bootAuthSession } from './lib/session.js';
import { renderDuesMemberStatus, renderDuesPendingPayment } from './lib/dues-member-status.js';
import { renderSevaBrandFooter } from './lib/event-register-chrome.js';
import { SEVA_EVENTS, type OpenDuesDetail } from './lib/events.js';
import {
	loadDuesCtaState,
	resolveDuesCtaActionCopy,
	resolveDuesCtaRenderMode,
	shouldShowDuesAction,
	type DuesCtaView
} from './lib/dues-cta-state.js';
import { DEFAULT_API_URL } from './lib/constants.js';
import type {
	DuesMemberSnapshot,
	DuesOptionsResponse,
	DuesPaymentMethodSnapshot
} from './lib/dues-types.js';

export const DUES_PAY_LINK_PARAM = 'seva-dues';
export const DUES_PAY_LINK_VALUE = 'pay';

@customElement('seva-dues-cta')
export class SevaDuesCta extends SevaFreezable(LitElement) {
	static override styles = duesCtaStyles;

	@property({ attribute: 'tenant-slug' }) tenantSlug = '';
	@property({ attribute: 'api-url' }) apiUrl = DEFAULT_API_URL;
	@property({ attribute: 'return-url' }) returnUrl = '';
	@property() theme: 'light' | 'dark' = 'light';

	@state() private view: DuesCtaView = 'loading';
	@state() private error = '';
	@state() private options: DuesOptionsResponse | null = null;
	@state() private memberSnapshot: DuesMemberSnapshot | null = null;
	@state() private paymentMethod: DuesPaymentMethodSnapshot | null = null;

	private tokenManager!: TokenManager;
	private api!: ApiClient;
	private loadGeneration = 0;
	private resetTokenDispatched = false;
	private payLinkDispatched = false;

	override connectedCallback(): void {
		super.connectedCallback();
		this.tokenManager = new TokenManager(`seva-auth-${this.tenantSlug}`);
		this.api = new ApiClient(this.apiUrl, this.tenantSlug, () => this.tokenManager.getToken());

		document.addEventListener('seva:authenticated', this.onAuthenticated);
		document.addEventListener('seva:signed-out', this.handleSignedOut);
		document.addEventListener(
			SEVA_EVENTS.DUES_CHECKOUT_COMPLETE,
			this.handleCheckoutComplete as EventListener
		);
		document.addEventListener(
			SEVA_EVENTS.DUES_PENDING_STATUS,
			this.handlePendingStatusHandoff as EventListener
		);
		document.addEventListener(
			SEVA_EVENTS.DUES_COMPLETE_DISMISS,
			this.handleCompleteDismiss as EventListener
		);

		void this.init();
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		document.removeEventListener('seva:authenticated', this.onAuthenticated);
		document.removeEventListener('seva:signed-out', this.handleSignedOut);
		document.removeEventListener(
			SEVA_EVENTS.DUES_CHECKOUT_COMPLETE,
			this.handleCheckoutComplete as EventListener
		);
		document.removeEventListener(
			SEVA_EVENTS.DUES_PENDING_STATUS,
			this.handlePendingStatusHandoff as EventListener
		);
		document.removeEventListener(
			SEVA_EVENTS.DUES_COMPLETE_DISMISS,
			this.handleCompleteDismiss as EventListener
		);
	}

	private async init(): Promise<void> {
		await this.bootAuth();
		await this.loadOptions();

		if (
			!this.resetTokenDispatched &&
			new URL(window.location.href).searchParams.has('seva-reset-token')
		) {
			this.resetTokenDispatched = true;
			this.dispatchOpenDues({ view: 'sign-in' });
			return;
		}

		this.openFromPayLink();
	}

	/**
	 * Plan 056 decision 7 — dues emails link to the member site with
	 * ?seva-dues=pay. Open the tray (it handles sign-in, then shows what is
	 * owed) and strip the param so a reload or shared URL does not reopen it.
	 */
	private openFromPayLink(): void {
		if (this.payLinkDispatched) return;
		const url = new URL(window.location.href);
		if (url.searchParams.get(DUES_PAY_LINK_PARAM) !== DUES_PAY_LINK_VALUE) return;
		this.payLinkDispatched = true;
		url.searchParams.delete(DUES_PAY_LINK_PARAM);
		window.history.replaceState(window.history.state, '', url.toString());
		this.dispatchOpenDues();
	}

	private async bootAuth(): Promise<void> {
		const result = await bootAuthSession(this.tokenManager, this.api);
		if (result && 'user' in result) {
			document.dispatchEvent(
				new CustomEvent('seva:authenticated', {
					bubbles: true,
					composed: true,
					detail: { user: result.user, token: result.token }
				})
			);
		} else if (result && 'error' in result) {
			this.error = result.error;
		}
	}

	private handleSignedOut = (): void => {
		this.tokenManager.clear();
		this.options = null;
		this.memberSnapshot = null;
		this.paymentMethod = null;
		this.error = '';
		void this.loadOptions();
	};

	private onAuthenticated = (e: Event): void => {
		const detail = (e as CustomEvent).detail;
		if (detail?.token) {
			this.tokenManager.setAuth(detail.user, detail.token);
			void this.loadOptions();
		}
	};

	private reloadDuesState = (): void => {
		void this.loadOptions();
	};

	private handleCheckoutComplete = (): void => {
		this.reloadDuesState();
	};

	private handlePendingStatusHandoff = (): void => {
		this.reloadDuesState();
	};

	private handleCompleteDismiss = (): void => {
		this.reloadDuesState();
	};

	private handleInvalidSession(): void {
		this.tokenManager.clear();
		document.dispatchEvent(new CustomEvent('seva:signed-out', { bubbles: true }));
		this.view = 'action';
		this.error = '';
	}

	private async loadOptions(): Promise<void> {
		const gen = ++this.loadGeneration;
		this.view = 'loading';
		this.error = '';
		this.memberSnapshot = null;
		this.options = null;
		this.paymentMethod = null;

		const authed = Boolean(this.tokenManager.getToken());
		if (!authed) {
			if (gen === this.loadGeneration) this.view = 'action';
			return;
		}

		const state = await loadDuesCtaState(this.api, { authed });
		if (gen !== this.loadGeneration) return;

		if (state.invalidSession) {
			this.handleInvalidSession();
			return;
		}

		this.options = state.options;
		this.memberSnapshot = state.memberSnapshot;
		this.paymentMethod = state.paymentMethod;
		this.error = state.error;
		this.view = state.view;
	}

	private dispatchOpenDues(detail: OpenDuesDetail = {}): void {
		document.dispatchEvent(
			new CustomEvent(SEVA_EVENTS.OPEN_DUES, { bubbles: true, composed: true, detail })
		);
	}

	private handleUpdatePaymentMethod = (): void => {
		const duesMembershipId = this.paymentMethod?.duesMembershipId;
		if (!duesMembershipId) return;
		this.dispatchOpenDues({ view: 'update-payment-method', duesMembershipId });
	};

	private handlePrimaryClick = (): void => {
		this.dispatchOpenDues();
	};

	private renderAction(): TemplateResult {
		const { heading, buttonLabel } = resolveDuesCtaActionCopy(this.options?.mode);
		return html`<div class="card">
			<h2 class="dues-heading dues-cta-heading">${heading}</h2>
			<button class="btn-primary" @click=${this.handlePrimaryClick}>${buttonLabel}</button>
			${this.paymentMethod
				? html`<button
						type="button"
						class="btn-outline update-pm-btn"
						@click=${this.handleUpdatePaymentMethod}
					>
						Update payment method
					</button>`
				: nothing}
			${renderSevaBrandFooter()}
		</div>`;
	}

	private renderBody(): TemplateResult {
		if (this.view === 'pending-payment') {
			return renderDuesPendingPayment(this.options?.pendingPayment);
		}
		if (this.view === 'member-status') {
			return renderDuesMemberStatus(this.memberSnapshot, {
				paymentMethod: this.paymentMethod,
				onUpdatePaymentMethod: this.handleUpdatePaymentMethod
			});
		}
		if (shouldShowDuesAction(this.view)) {
			return this.renderAction();
		}
		return html`<div class="card">
			<p>No dues payment is available right now.</p>
			${renderSevaBrandFooter()}
		</div>`;
	}

	override render() {
		const mode = resolveDuesCtaRenderMode({ view: this.view, error: this.error });
		switch (mode) {
			case 'loading':
				return html`<div class="card">Loading dues…${renderSevaBrandFooter()}</div>`;
			case 'error-only':
				return html`<div class="alert alert-error">${this.error}</div>`;
			case 'body':
				return html`${this.error
					? html`<div class="alert alert-error">${this.error}</div>`
					: nothing}
				${this.renderBody()}`;
			default:
				return nothing;
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'seva-dues-cta': SevaDuesCta;
	}
}
